import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, fmtMoney, formatApiError } from "../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Portfolio() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [txs, setTxs] = useState([]);
  const [series, setSeries] = useState([]);
  const [polling, setPolling] = useState(null);
  const [params, setParams] = useSearchParams();

  const loadAll = () => {
    api.get("/portfolio").then((r) => setStats(r.data));
    api.get("/transactions").then((r) => setTxs(r.data || []));
  };

  useEffect(() => {
    loadAll();
    api.get("/market/ucs-history?days=30").then((r) => {
      const s = (r.data.series || []).map((d) => ({
        date: new Date(d.t).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        price: d.p,
      }));
      setSeries(s);
    });
  }, []);

  // Poll on return from Stripe (direct UCS purchase)
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    setPolling(t("portfolio.stripe_confirming"));
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setPolling(null);
          if (data.purpose === "ucs_purchase" && data.ucs_tons_added) {
            toast.success(`Purchased ${data.ucs_tons_added.toFixed(3)} tCO₂ from ${data.project_name || "project"}.`);
          } else {
            toast.success("Payment confirmed.");
          }
          setParams({});
          loadAll();
          return;
        }
        if (data.status === "expired" || attempts >= 10) {
          setPolling(null);
          toast.error("Payment not completed.");
          setParams({});
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        setPolling(null);
        toast.error(formatApiError(e?.response?.data?.detail));
        setParams({});
      }
    };
    tick();
  }, []);

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="label-tag mb-4">{t("portfolio.tag")}</div>
            <h1 className="font-serif text-5xl tracking-tight">{t("portfolio.title")}</h1>
          </div>
          <Link
            to="/alerts"
            data-testid="portfolio-alerts-cta"
            className="btn-outline !px-5 !py-2.5 !text-xs whitespace-nowrap"
          >
            {t("portfolio.set_alerts")} →
          </Link>
        </div>
        {polling && (
          <div className="mt-6 inline-block px-4 py-2 border border-ink bg-muted/40 text-sm font-mono" data-testid="portfolio-polling">
            {polling}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-line border border-line">
        <Tile label={t("portfolio.ucs_holdings")} value={`${(stats?.ucs_tons || 0).toFixed(3)}`} unit={t("portfolio.tco2")} />
        <Tile label={t("portfolio.invested")} value={fmtMoney(stats?.total_invested_usd || 0, "USD")} unit={t("portfolio.usd")} />
        <Tile label={t("portfolio.co2_offset")} value={`${(stats?.co2_offset_tons || 0).toFixed(2)}`} unit={t("portfolio.tonnes")} />
        <Tile label={t("portfolio.cars_off_road")} value={`${stats?.cars_off_road_per_year || 0}`} unit={t("portfolio.equiv")} />
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 border border-line bg-surface p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-tag">{t("portfolio.ucs_esg_index_30d")}</div>
              <div className="font-serif text-2xl mt-1">{t("portfolio.ucs_full")}</div>
            </div>
            <div className="text-xs font-mono text-ink2">{t("portfolio.source_bmv")}</div>
          </div>
          <div style={{ width: "100%", height: 280, minHeight: 280 }}>
            <ResponsiveContainer>
              <LineChart data={series}>
                <CartesianGrid stroke="#E5E2DC" vertical={false} />
                <XAxis dataKey="date" stroke="#59665F" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                <YAxis stroke="#59665F" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #1C2B23", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }} />
                <Line type="monotone" dataKey="price" stroke="#1C2B23" strokeWidth={2} dot={false} animationDuration={1200} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-4 border border-line bg-surface p-6">
          <div className="label-tag">{t("portfolio.impact_equivalents")}</div>
          <div className="font-serif text-2xl mt-1">{t("portfolio.what_it_means")}</div>
          <ul className="mt-6 space-y-4 text-sm">
            <Equiv k={t("portfolio.trees_planted")} v={Math.round(stats?.trees_equiv || 0)} />
            <Equiv k={t("portfolio.cars_removed")} v={`${stats?.cars_off_road_per_year || 0}`} />
            <Equiv k={t("portfolio.transactions")} v={stats?.transactions_count || 0} />
          </ul>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 my-12">
        <div className="label-tag mb-3">{t("portfolio.transactions")}</div>
        <div className="border border-line bg-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <Th>{t("portfolio.th_date")}</Th><Th>{t("portfolio.th_type")}</Th><Th>{t("portfolio.th_project")}</Th>
                <Th>{t("portfolio.th_tons")}</Th><Th>{t("portfolio.th_amount")}</Th><Th>{t("portfolio.th_currency")}</Th>
              </tr>
            </thead>
            <tbody>
              {txs.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-ink2">{t("portfolio.no_tx")}</td></tr>
              )}
              {txs.map((t) => (
                <tr key={t.id} className="border-b border-line/60" data-testid={`tx-${t.id}`}>
                  <Td className="text-ink2 mono-num text-xs">{new Date(t.created_at).toLocaleString()}</Td>
                  <Td><span className={`px-2 py-0.5 text-[10px] font-mono uppercase ${t.type === "buy" ? "bg-leaf text-white" : "bg-muted text-ink"}`}>{t.type}</span></Td>
                  <Td>{t.project_name || "—"}</Td>
                  <Td className="mono-num">{t.tons ? Number(t.tons).toFixed(3) : "—"}</Td>
                  <Td className="mono-num">{fmtMoney(t.amount || 0, t.currency)}</Td>
                  <Td className="mono-num text-ink2 text-xs">{t.currency}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}

function Tile({ label, value, unit }) {
  return (
    <div className="bg-surface p-6" data-testid={`tile-${label}`}>
      <div className="label-tag">{label}</div>
      <div className="mono-num text-3xl mt-2 text-ink">{value}</div>
      <div className="text-xs text-ink2 mt-1">{unit}</div>
    </div>
  );
}
function Equiv({ k, v }) {
  return (
    <li className="flex justify-between border-b border-line/50 pb-3">
      <span className="text-ink2">{k}</span>
      <span className="mono-num text-ink">{v}</span>
    </li>
  );
}
function Th({ children }) { return <th className="px-4 py-3 text-[11px] font-sans tracking-[0.2em] uppercase text-ink2">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
